import { inject, Injectable, WritableSignal } from '@angular/core';

import { KeyValueType } from '@models/common';
import { GenericObjectType } from '@models/helpers';
import { Measurements, MeasurementsConversion, MeasurementSystem, MeasurementType } from '@models/module/fields/enums';
import { AttachmentModel, BaseFieldValueType } from '@models/module/fields/main';
import { Currency } from '@models/system';
import { CipoFieldConfig, CipoTextViewConfig } from '@shared/components/fields/common';
import { ACCENT_BG_COLOR, WARN_BG_COLOR } from '@shared/consts/color.enum';
import { UserStore } from '@signal-store/index';

// It might be a better idea to have individual helper functions, instead of a service (to reduce bundle size).
// Unless we need to inject dependencies, in which case a service is the way to go.
@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  userStore = inject(UserStore);

  private currencySymbolDefault = '$';
  private leftSideDefault = true;
  private noSpaceDefault = true;

  /**
   * Returns the inverted hex color (used for dark/light mode)
   * Accepts a string of 6 elements, or 7 elements if starting with 'x'
   *
   * @param {string} hexColor
   * @returns {string} If hexColor starts with 'x', returns new hex that also starts with 'x'
   */
  invertHex(hexColor: string): string {
    if (hexColor.length === 6) {
      return (Number(`0x1${hexColor}`) ^ 0xffffff).toString(16).toUpperCase();
    } else if (hexColor.startsWith('#') && hexColor.length === 7) {
      return `#${(Number(`0x1${hexColor.substring(1)}`) ^ 0xffffff).toString(16).toUpperCase()}`;
    } else {
      throw new Error('Invalid hex color');
    }
  }

  /**
   * Replace all spaces within a string with underscore
   *
   * @param {string} text string that might have spaces, ex: 'my string'
   * @returns {string} the updated string, ex: 'my_string'
   */
  formatText(text: string): string {
    return text.replace(/ /g, '_');
  }

  /**
   * Receives an object of keyValue pairs, and outputs an array of objects
   *
   * @param {{ [key: string]: string }} obj
   * @returns {{key: string, value: string}[]} KeyValueType[]
   */
  mapObjectToArray(obj: GenericObjectType): KeyValueType[] {
    return Object.keys(obj).map(key => ({ key: key, value: obj[key] }));
  }

  /**
   * Receives an array of objects, and outputs an object of keyValue pairs
   *
   * @param {{key: string, value: string}[]} array
   * @returns {{ [key: string]: string }} GenericObjectType
   */
  mapArrayToObject(array: KeyValueType[]): GenericObjectType {
    const obj: GenericObjectType = {};
    array.forEach(element => (obj[element.key] = element.value));
    return obj;
  }

  /**
   * Converts a number that was formatted with .toLocaleString() back to a number, using the locale calculated from decimalSeparator and thousandSeparator
   *
   * @param {string} value
   * @returns {number}
   */
  revertLocaleNumber(value: string, decimalSeparator: string, thousandSeparator: string): number | undefined {
    const baseValue = value.replaceAll(thousandSeparator, '').replace(decimalSeparator, '.');
    const newValue = parseFloat(baseValue);
    return isNaN(newValue) ? undefined : newValue;
  }

  /**
   * Converts a number to a string using a locale calculated from given decimalSeparator and thousandSeparator.
   * When value is a string (raw user input), trailing decimal separator and trailing zeros are preserved
   * so that in-progress input like "1." or "1.0" is not collapsed to "1" while the user is still typing.
   *
   * @param {number | string} value
   * @param {number} decimals
   * @param {string} decimalSeparator
   * @param {string} thousandSeparator
   * @param {boolean} showThousandSeparator
   * @param {boolean} enforceDecimals, when true will always show decimals, even if they are 0
   * @returns {string}
   */
  formatLocaleNumber(
    value: number | string,
    decimals: number = 0,
    decimalSeparator: string = '.',
    thousandSeparator: string = ',',
    showThousandSeparator: boolean = false,
    enforceDecimals: boolean = false,
  ): string {
    let rawInput: string | undefined;
    let numericValue: number;
    if (typeof value === 'string') {
      rawInput = value;
      numericValue = parseFloat(value.replaceAll(thousandSeparator, '').replace(decimalSeparator, '.'));
      if (isNaN(numericValue)) return value;
    } else {
      numericValue = value;
    }

    const numberLocale = this.getNumberLocaleFromDecimalAndThousandSeparators(decimalSeparator, thousandSeparator);
    const options = {
      maximumFractionDigits: decimals,
      useGrouping: showThousandSeparator,
    } as Intl.NumberFormatOptions;
    if (enforceDecimals) {
      options.minimumFractionDigits = decimals;
    }
    // truncatedValue is used to avoid rounding up the number, for example 1.999 with 2 decimals should be 1.99 and not 2.00
    const truncatedValue = this.getTruncatedNumber(numericValue, decimals);
    const result = new Intl.NumberFormat(numberLocale, options).format(truncatedValue);

    return rawInput != null ? this.appendTrailingDecimalChars(result, rawInput, decimalSeparator, decimals) : result;
  }

  // Preserves trailing decimal separator or trailing zeros the user has already typed,
  // e.g. "1." stays "1.", "1.0" stays "1.0", "1.10" stays "1.10".
  private appendTrailingDecimalChars(
    result: string,
    rawInput: string,
    decimalSeparator: string,
    decimals: number,
  ): string {
    if (decimals <= 0 || !rawInput.includes(decimalSeparator)) {
      return result;
    }
    if (rawInput.endsWith(decimalSeparator)) {
      return `${result}${decimalSeparator}`;
    }
    const rawAfterDecimal = rawInput.slice(rawInput.indexOf(decimalSeparator) + 1);
    const trailingZeroMatch = rawAfterDecimal.match(/0+$/);
    if (!trailingZeroMatch) {
      return result;
    }
    const resultDecimalIdx = result.indexOf(decimalSeparator);
    const existingDecimalDigits = resultDecimalIdx === -1 ? 0 : result.length - resultDecimalIdx - 1;
    const totalRawDecimalDigits = Math.min(rawAfterDecimal.length, decimals);
    const zerosToAppend = trailingZeroMatch[0].slice(0, totalRawDecimalDigits - existingDecimalDigits);
    if (zerosToAppend.length === 0) {
      return result;
    }
    return `${result}${result.includes(decimalSeparator) ? '' : decimalSeparator}${zerosToAppend}`;
  }

  private getTruncatedNumber(value: number, decimals: number): number {
    // Convert to a dot-separated string via en-US locale (avoids scientific notation and IEEE 754 multiplication errors).
    // e.g. 2575.68 * 100 = 257567.99999999997, so we slice the string instead of multiplying.
    const str = value.toLocaleString('en-US', { maximumFractionDigits: 20, useGrouping: false });
    const dotIdx = str.indexOf('.');
    const truncatedValue =
      dotIdx === -1 || decimals === 0 ? Math.trunc(value) : parseFloat(str.slice(0, dotIdx + decimals + 1));
    return truncatedValue;
  }

  /**
   * Updates the fieldConfig with the currency prefix and suffix
   * @param {CipoFieldConfig} fieldConfig
   * @returns {CipoFieldConfig}
   */
  updateCipoFieldConfigIfCurrencyIsSet(fieldConfig: CipoFieldConfig): CipoFieldConfig {
    if (!fieldConfig.currency) {
      return fieldConfig;
    }
    const updatedData = {
      ...fieldConfig,
      prefix: this.getCurrencyPrefix(fieldConfig.currency),
      suffix: this.getCurrencySuffix(fieldConfig.currency),
    };
    return updatedData;
  }

  /**
   * Convert multiple types of true to boolean;
   * @param value
   * @returns {boolean}
   */
  convertValueToBoolean(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      value = value.toLocaleLowerCase();
    }

    switch (value) {
      case true:
      case 'true':
      case 1:
      case '1':
      case 'on':
      case 'yes':
        return true;
      default:
        return false;
    }
  }

  /**
   * Transforms a number or string value into a formatted currency or number string.
   * @param value - The input value to be transformed.
   * @param decimals - The number of decimal places to display (default: 0).
   * @param showComma - Whether to show thousand separators (default: true).
   * @param showCurrency - Whether to display the currency symbol (default: false).
   * @param showPercentage - Whether to display as a percentage (default: false).
   * @param currency - The currency object containing symbol and formatting options (optional).
   * @param percentageSymbol - The symbol to use for percentage (default: '%').
   * @returns An Observable that emits the formatted string.
   */
  public transformToNumberOrCurrencyString(
    value: number | string | null | undefined,
    decimals: number = 0,
    showComma: boolean = true,
    showCurrency: boolean = false,
    showPercentage: boolean = false,
    currency: Currency | null | undefined = undefined,
    percentageSymbol: string = '%',
  ): string {
    const userSettings = this.userStore.calculatedUserSettings();
    return this.convertValueToNumberOrCurrencyString(
      value,
      decimals,
      showComma,
      showCurrency,
      showPercentage,
      currency,
      percentageSymbol,
      userSettings?.decimalSeparator,
      userSettings.thousandSeparator,
    );
  }

  /**
   * Converts a number or string value into a formatted currency or number string.
   * @param value - The input value to be transformed.
   * @param decimals - The number of decimal places to display (default: 0).
   * @param showComma - Whether to show thousand separators (default: true).
   * @param showCurrency - Whether to display the currency symbol (default: false).
   * @param showPercentage - Whether to display as a percentage (default: false).
   * @param currency - The currency object containing symbol and formatting options (optional).
   * @param percentageSymbol - The symbol to use for percentage (default: '%').
   * @param decimalSeparator - The decimal separator.
   * @param thousandSeparator - The thousand separator.
   * @returns An Observable that emits the formatted string.
   */
  public convertValueToNumberOrCurrencyString(
    value: number | string | null | undefined,
    decimals: number = 0,
    showComma: boolean = true,
    showCurrency: boolean = false,
    showPercentage: boolean = false,
    currency: Currency | null | undefined = undefined,
    percentageSymbol: string = '%',
    decimalSeparator: string = '.',
    thousandSeparator: string = ',',
  ): string {
    if (value === undefined || value === null) {
      return '';
    }

    if (showCurrency && value?.toString().includes(currency?.symbol ?? this.currencySymbolDefault)) {
      return value.toString();
    } else if (showPercentage && value?.toString().includes(percentageSymbol)) {
      return value.toString();
    }

    let newValue = value;
    const numberValue: number = typeof newValue == 'string' ? Number((newValue as any)?.toString()) : newValue;
    if (numberValue === undefined || numberValue === null || isNaN(numberValue)) {
      return value?.toString();
    }

    if (showCurrency && (currency === null || currency === undefined)) {
      currency = { symbol: this.currencySymbolDefault } as Currency;
    }

    const numberValueAbs: number = Math.abs(numberValue);
    const ret = this.formatLocaleNumber(
      numberValueAbs,
      decimals,
      decimalSeparator,
      thousandSeparator,
      showComma,
      showCurrency, // enforceDecimals only if it's a currency
    );
    const prefixNegative = numberValue < 0 ? '-' : '';

    if (showPercentage) {
      return `${prefixNegative}${ret}${percentageSymbol}`;
    }

    if (showCurrency) {
      const prefixCurrency = this.getCurrencyPrefix(currency, prefixNegative);
      const suffixCurrency = this.getCurrencySuffix(currency);
      return `${prefixCurrency}${ret}${suffixCurrency}`;
    }

    return `${prefixNegative}${ret}`;
  }

  /**
   * Converts a value from one measurement system to another and returns it as a formatted string with the appropriate measurement unit.
   * @param value - The value to be converted.
   * @param measurementType - The type of measurement (e.g., length, weight).
   * @param measurementSystem - The target measurement system.
   * @param convertFromMeasurementSystem - The source measurement system (optional, default: 'Metric').
   * @returns The converted value as a formatted string with the measurement unit.
   */
  public convertValueToMeasurementString(
    value: number | string | null | undefined,
    measurementType: MeasurementType,
    measurementSystem: keyof typeof MeasurementSystem,
    convertFromMeasurementSystem?: keyof typeof MeasurementSystem,
  ): string {
    let measurementUnit = '';
    if (typeof measurementSystem === 'string') {
      measurementUnit = this.getMeasurementUnit(measurementType, measurementSystem) ?? '';
      convertFromMeasurementSystem = convertFromMeasurementSystem ?? 'Metric';

      // convert the value to the target measurement system
      value = this.convertValueToMeasurementValue(
        value,
        measurementType,
        convertFromMeasurementSystem,
        measurementSystem,
      );
    }

    // we want to show the value without the decimal if the decimal is zero
    // for this we have to verify the decimal part after conversion as a number like 27.967539 returns as 28.0
    const numericValue = Number.parseFloat(value.toString());
    const roundedToOneDecimal = Math.round(numericValue * 10) / 10;
    const decimals = Number.isInteger(roundedToOneDecimal) ? 0 : 1;
    value = this.transformToNumberOrCurrencyString(value, decimals);

    if (value !== undefined && measurementUnit) {
      return `${value} ${measurementUnit}`;
    }
    return '';
  }

  /**
   * Converts a value from one measurement system to another and returns it as a number.
   * @param value - The value to be converted.
   * @param measurementType - The type of measurement (e.g., length, weight).
   * @param convertFromMeasurementSystem - The source measurement system.
   * @param convertToMeasurementSystem - The target measurement system.
   * @returns The converted value as a number.
   */
  public convertValueToMeasurementValue(
    value: number | string,
    measurementType: MeasurementType,
    convertFromMeasurementSystem: keyof typeof MeasurementSystem,
    convertToMeasurementSystem: keyof typeof MeasurementSystem,
  ): number {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
      return undefined;
    }

    if (convertFromMeasurementSystem === convertToMeasurementSystem) {
      return Number(value);
    }

    // convert the value to the target measurement system
    return MeasurementsConversion[convertFromMeasurementSystem][convertToMeasurementSystem][measurementType](
      Number(value),
    );
  }

  /**
   * Gets the measurement unit for a given measurement type and system.
   * @param measurementType - The type of measurement (e.g., temperature, speed).
   * @param measurementSystem - The measurement system (e.g., Metric, Imperial).
   * @returns The measurement unit as a string.
   */
  public getMeasurementUnit(
    measurementType: MeasurementType,
    measurementSystem: keyof typeof MeasurementSystem,
  ): string {
    if (typeof measurementSystem === 'string') {
      return Measurements[measurementSystem][measurementType] ?? '';
    }
    return '';
  }

  insertElementInAlphabeticallyArray<T>(elements: T[], newElement: T, property: string) {
    // Find insertion point using binary search
    let low = 0;
    let high = elements.length - 1;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const comparison = newElement[property].localeCompare(elements[mid][property]);

      if (comparison < 0) {
        high = mid - 1;
      } else {
        low = mid + 1;
      }
    }

    //Insert at the correct position
    const updatedElements = [...elements];
    updatedElements.splice(low, 0, newElement);
    return updatedElements;
  }

  moveItemsInSignalArray(elements: WritableSignal<unknown[]>, previousIndex: number, currentIndex: number) {
    elements.update(array => {
      if (previousIndex < 0 || previousIndex >= array.length || currentIndex < 0 || currentIndex >= array.length) {
        throw new Error('Index out of bounds');
      }

      const newArray = [...array]; // Create a copy to maintain immutability
      const itemToMove = newArray.splice(previousIndex, 1)[0]; // Remove item from previous index
      newArray.splice(currentIndex, 0, itemToMove); // Insert item at new index

      return newArray;
    });
  }

  mergeObjects<T extends object, U extends object>(
    obj1: T | undefined,
    obj2: U | undefined,
    overrideValues: boolean = false,
  ): T & U {
    // Handle undefined objects
    if (!obj1 && !obj2) {
      throw new Error('Both objects cannot be undefined');
    }

    // Use empty object if obj1 is undefined
    const result = obj1 ? ({ ...obj1 } as T & U) : ({} as T & U);

    // If obj2 is undefined, just return the result
    if (!obj2) {
      return result;
    }

    for (const key in obj2) {
      if (Object.prototype.hasOwnProperty.call(obj2, key)) {
        // If obj2's value is undefined or null, don't override existing values
        if (obj2[key] === undefined || obj2[key] === null) {
          continue;
        }

        // If overrideValues is true OR the property in obj1 is empty/undefined
        if (overrideValues || !result[key]) {
          (result as any)[key] = obj2[key];
        }
      }
    }

    return result;
  }

  /**
   * Checks if an item would create a duplicate in the list based on specified properties
   * @param list The array of existing items
   * @param item The new or edited item to check
   * @param propertiesToCheck Properties to check for duplicates
   * @param checkAllProperties If true, all properties must match; if false, any property match is considered a duplicate
   * @param idProperty The property name used as unique identifier (default: 'id')
   * @returns boolean indicating if a duplicate was found
   */
  checkDuplicate<T>(
    list: T[],
    item: T,
    propertiesToCheck: string[],
    checkAllProperties: boolean = true,
    idProperty: string = 'id',
  ): boolean {
    return list.some(existingItem => {
      // Skip comparing with itself when editing an existing item
      // This checks if we're dealing with the same entity by comparing IDs
      if (
        item[idProperty] !== undefined &&
        existingItem[idProperty] !== undefined &&
        existingItem[idProperty] === item[idProperty]
      ) {
        return false;
      }

      if (checkAllProperties) {
        // All properties must match (AND condition)
        return propertiesToCheck.every(
          prop => existingItem[prop] !== undefined && item[prop] !== undefined && existingItem[prop] === item[prop],
        );
      } else {
        // Any property must match (OR condition)
        return propertiesToCheck.some(
          prop => existingItem[prop] !== undefined && item[prop] !== undefined && existingItem[prop] === item[prop],
        );
      }
    });
  }

  private getCurrencyPrefix(currency: Currency, prefixNegative?: string): string {
    const leftSide = currency?.leftSide ?? this.leftSideDefault;
    const noSpace = currency?.noSpace ?? this.noSpaceDefault;
    const symbol = currency?.symbol ?? this.currencySymbolDefault;

    const prefixSpace = !noSpace && leftSide ? ' ' : '';
    const prefixCurrency = leftSide ? symbol : '';
    prefixNegative = prefixNegative ?? '';

    if (noSpace) {
      return `${prefixNegative}${prefixCurrency}`;
    }

    return `${prefixCurrency}${prefixSpace}${prefixNegative}`;
  }

  private getCurrencySuffix(currency: Currency): string {
    const leftSide = currency?.leftSide ?? this.leftSideDefault;
    const noSpace = currency?.noSpace ?? this.noSpaceDefault;
    const symbol = currency?.symbol ?? this.currencySymbolDefault;

    const suffixSpace = !noSpace && !leftSide ? ' ' : '';
    const suffixCurrency = !leftSide ? symbol : '';

    if (noSpace) {
      return suffixCurrency;
    }

    return `${suffixSpace}${suffixCurrency}`;
  }

  private getNumberLocaleFromDecimalAndThousandSeparators(decimalSeparator: string, thousandSeparator: string): string {
    if (decimalSeparator === ',' && thousandSeparator === '.') {
      return 'ro-RO';
    }
    return 'en-US';
  }

  /**
   * Get time progress based on start and end date;
   * @param {string} start
   * @param {string} end
   * @returns {number} percentage
   */
  //   not used for now, but might be useful in the future
  //   getTimeProgress(start: string, end: string): number {
  //     const startDate = new Date(start);
  //     const endDate = new Date(end);
  //     const now = new Date();
  //     if (!startDate.getTime() || !endDate.getTime() || startDate.getTime() > endDate.getTime()) {
  //       return null;
  //     }
  //     const total = endDate.getTime() - startDate.getTime();
  //     const elapsed = now.getTime() - startDate.getTime();
  //     return Math.round((elapsed / total) * 100);
  //   }

  /**
   * Performs a deep comparison between two values to determine if they are equivalent.
   * Supports comparison of primitives, arrays, and nested objects.
   *
   * @param {*} a - The first value to compare.
   * @param {*} b - The second value to compare.
   * @param {Object} [options] - Optional comparison settings.
   * @param {boolean} [options.ignoreArrayOrder=false] - If true, arrays are considered equal regardless of element order.
   * @returns {boolean} `true` if the values are deeply equal, `false` otherwise.
   *
   * @example
   * deepEqual({ a: 1, b: [2, 3] }, { b: [3, 2], a: 1 }, { ignoreArrayOrder: true }); // true
   *
   * @example
   * deepEqual({ a: 1, b: [2, 3] }, { b: [3, 2], a: 1 }); // false
   */
  deepEqual(a: any, b: any, options: { ignoreArrayOrder?: boolean } = {}): boolean {
    const { ignoreArrayOrder = true } = options;

    if (a === b) return true;

    if (typeof a !== typeof b) return false;

    if (a === null || b === null) return a === b;

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;

      if (ignoreArrayOrder) {
        const visited = new Array(b.length).fill(false);
        return a.every(itemA => {
          for (let i = 0; i < b.length; i++) {
            if (!visited[i] && this.deepEqual(itemA, b[i], options)) {
              visited[i] = true;
              return true;
            }
          }
          return false;
        });
      } else {
        return a.every((item, index) => this.deepEqual(item, b[index], options));
      }
    }

    if (typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;

      return keysA.every(key => b.hasOwnProperty(key) && this.deepEqual(a[key], b[key], options));
    }

    return false;
  }

  /**
   * Get keyValue pairs from enum
   * @param {object} enumObj
   * @param {function} mapValue Function to map the value, defaults to converting to string
   * @returns {KeyValueType<number>[]} Array of keyValue pairs
   */
  getEnumKeyValuePairs(
    enumObj: object,
    mapValue: Function = ({ key, value }) => value.toString(),
  ): KeyValueType<number>[] {
    return Object.entries(enumObj)
      .filter(([key, _]) => !!Number(key)) // Filter out reverse mappings
      .map(([key, value]) => ({ key: Number(key), value: mapValue({ key, value }) }));
  }

  /**
   * Compares two arrays and returns the items that are present in both, only in the first array, and only in the second array.
   * @param {T[]} firstArr - The first array to compare.
   * @param {T[]} secondArr - The second array to compare.
   * @param {string} [prop] - Optional property name to compare objects by. If not provided, compares by reference.
   * @returns {{ both: T[], firstOnly: T[], secondOnly: T[] }} An object containing three arrays:
   * - `both`: Items present in both arrays.
   * - `firstOnly`: Items only in the first array.
   * - `secondOnly`: Items only in the second array.
   */
  diffArrays<T = unknown>(
    firstArr: T[],
    secondArr: T[],
    prop?: string,
  ): { both: T[]; firstOnly: T[]; secondOnly: T[] } {
    const firstMap = new Map(firstArr.map(item => [this.getDiffItemByProp(item, prop), item]));
    const secondMap = new Map(secondArr.map(item => [this.getDiffItemByProp(item, prop), item]));

    const both: T[] = [];
    const firstOnly: T[] = [];
    const secondOnly: T[] = [];

    for (const [id, oldItem] of firstMap.entries()) {
      if (secondMap.has(id)) {
        both.push(oldItem);
      } else {
        firstOnly.push(oldItem);
      }
    }

    for (const [id, newItem] of secondMap.entries()) {
      if (!firstMap.has(id)) {
        secondOnly.push(newItem);
      }
    }

    return { both, firstOnly, secondOnly };
  }

  /**
   * Compares two objects and returns the properties that are present in both, only in the first object, and only in the second object.
   * If a prop is provided, it compares the values of that prop instead of the whole object.
   * @param oldObj The first object to compare.
   * @param newObj The second object to compare.
   * @param prop Optional property name to compare objects by. If not provided, compares the whole object.
   * @returns An object containing three properties:
   * - `both`: Properties present in both objects.
   * - `firstOnly`: Properties only in the first object.
   * - `secondOnly`: Properties only in the second object.
   */
  diffObjects<T = unknown>(oldObj: T, newObj: T, prop?: string): { both: T; firstOnly: T; secondOnly: T } {
    if (Array.isArray(oldObj) && Array.isArray(newObj)) {
      return this.diffArrays(oldObj, newObj, prop) as unknown as { both: T; firstOnly: T; secondOnly: T };
    }

    const oldObjItem = this.getDiffItemByProp(oldObj, prop);
    const newObjItem = this.getDiffItemByProp(newObj, prop);
    if (!oldObjItem) {
      return { both: null, firstOnly: null, secondOnly: newObj };
    }
    if (!newObjItem) {
      return { both: null, firstOnly: oldObj, secondOnly: null };
    }
    if (this.deepEqual(newObjItem, oldObjItem)) {
      return { both: newObj, firstOnly: null, secondOnly: null };
    }
    return { both: null, firstOnly: oldObj, secondOnly: newObj };
  }

  /**
   * Compares old and new values, determines the differences, and returns a config object that can be used to set the value and readonlyConfig of a field.
   * If the values are arrays of attachments or addresses, it also sets the bgColor of the attachments to indicate which ones are new, removed, or unchanged.
   * @param oldValue The old value to compare.
   * @param newValue The new value to compare.
   * @returns An object containing the value and readonlyConfig for the field.
   */
  makeValueAndReadonlyConfig<T = unknown>(
    oldValue: T,
    newValue: T,
  ): {
    value?: BaseFieldValueType;
    readonlyConfig: CipoTextViewConfig;
  } {
    const isArray = Array.isArray(newValue) || Array.isArray(oldValue);
    const referenceArr = Array.isArray(newValue) && newValue.length ? newValue : oldValue;
    const referenceValue = isArray ? (referenceArr as any)?.[0] : newValue || oldValue;
    const isAttachment = referenceValue?.hasOwnProperty('versionId');
    const isAddress = referenceValue?.hasOwnProperty('locationId');
    const propertyKey = isAttachment ? 'id' : isAddress ? 'locationId' : undefined;

    const { both, firstOnly, secondOnly } = this.diffObjects(oldValue, newValue, propertyKey);

    if (isAttachment) {
      this.setWarnBgColorToAttachments(firstOnly as AttachmentModel | AttachmentModel[]);
      this.setAccentBgColorToAttachments(secondOnly as AttachmentModel | AttachmentModel[]);
    }

    return {
      value: both as BaseFieldValueType,
      readonlyConfig: {
        oldValue: firstOnly as BaseFieldValueType,
        newValue: secondOnly as BaseFieldValueType,
      },
    };
  }

  /**
   * Distinct array by key
   * @param {keyof T} key Key to check for distinct values
   * @param {T[]} array Array to check
   * @returns {T[]} Distinct array
   */
  distinctBy<T>(key: keyof T, array: T[]): T[] {
    const keys = array.map(value => value[key]);
    return array.filter((value, index) => keys.indexOf(value[key]) === index);
  }

  private getDiffItemByProp<T = unknown>(item: T, prop?: string): T {
    if (prop && typeof item === 'object' && item !== null) {
      return (item as Record<string, any>)[prop];
    }
    return item;
  }

  private setWarnBgColorToAttachments(attachments: AttachmentModel[] | AttachmentModel): void {
    this.setBgColorToAttachments(attachments, WARN_BG_COLOR);
  }

  private setAccentBgColorToAttachments(attachments: AttachmentModel[] | AttachmentModel): void {
    this.setBgColorToAttachments(attachments, ACCENT_BG_COLOR);
  }

  private setBgColorToAttachments(attachments: AttachmentModel[] | AttachmentModel, color: string): void {
    if (!attachments) {
      return;
    }
    if (Array.isArray(attachments)) {
      attachments.forEach(attachment => (attachment.bgColor = color));
    } else {
      attachments.bgColor = color;
    }
  }
}
